import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { get, put } from "@rails/request.js";
import cable from "@/lib/cable";


import SectionsAccordion from "./SectionsAccordion";
import ProjectForm from "./ProjectForm";
import SectionForm from "./SectionForm";
import SiteEditForm from "./siteEditForm";
import { useForm } from "react-hook-form";
import { getSiteCollaborators, getSiteVercelProjects } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";


const fetchSite = async (slug: string) => {
  const response = await get(`/sites/${slug}.json`, { responseKind: "json" });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error?.errors ? error.errors.join(", ") : "Failed to fetch site");
  }
  return response.json;
};

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

type Site = {
  id: number;
  name: string;
  subdomain: string;
  custom_domain: string;
  description: string;
  keywords: string;
  [key: string]: any;
};


const SiteShow: React.FC = () => {
  const { siteSlug } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [openProjectFormSectionId, setOpenProjectFormSectionId] = useState<number | null>(null);
  const [editingSection, setEditingSection] = useState<any>(null);
  const [editingProject, setEditingProject] = useState<any>(null);
  const [editingProjectSection, setEditingProjectSection] = useState<any>(null);
  const [deployLogs, setDeployLogs] = useState<string[]>([]);
  const [deployStatus, setDeployStatus] = useState<string | null>(null);
  const [deploySubmitMsg, setDeploySubmitMsg] = useState<string | null>(null);

  const { data: site, isLoading, error } = useQuery({
    queryKey: ["site", siteSlug],
    queryFn: () => fetchSite(siteSlug!),
    enabled: !!siteSlug,
  });

  useEffect(() => {
    if (!site?.id) return;
    // @ts-ignore
    const subscription = cable.subscriptions.create(
      { channel: "DeploymentsChannel", site_id: site.id },
      {
        received: (data: any) => {
          if (data.type === "log") {
            setDeployLogs((logs) => [...logs, data.log]);
          } else if (data.type === "state") {
            setDeployStatus(data.log);
          } else if (data.type === "deploy_submit") {
            setDeploySubmitMsg(data.log);
          }
        },
      }
    );
    return () => {
      // @ts-ignore
      cable.subscriptions.remove(subscription);
    };
  }, [site?.id]);

  return (
    <div className="">
      
      {/* Left: Edit form / Section form / Project form */}
      <div className="w-full">
        {isLoading && (
          <Card className="p-6">
            <Skeleton className="w-2/3 h-8 mb-4" />
            <Skeleton className="w-1/3 h-5 mb-2" />
            <Skeleton className="w-full h-4 mb-2" />
            <Skeleton className="w-1/2 h-4" />
          </Card>
        )}
        {error && (
          <Alert variant="destructive" className="mb-6">
            Error loading site: {(error as Error).message}
          </Alert>
        )}
        {site && (
          <>
            <Sheet open={!!showSectionForm || !!editingSection} onOpenChange={open => {
              if (!open) {
                setShowSectionForm(false);
                setEditingSection(null);
              }
            }}>
              <SheetContent side="right" className="max-w-lg w-full">
                <SheetHeader>
                  <SheetTitle>
                    {editingSection ? "Edit Section" : "New Section"}
                  </SheetTitle>
                </SheetHeader>
                {(showSectionForm || editingSection) && (
                  <SectionForm
                    siteSlug={site.slug}
                    section={editingSection || undefined}
                    onSectionCreated={() => {
                      queryClient.invalidateQueries({ queryKey: ["site", site.slug] });
                    }}
                    onClose={() => {
                      setShowSectionForm(false);
                      setEditingSection(null);
                    }}
                    onDeleted={() => {
                      queryClient.invalidateQueries({ queryKey: ["site", site.slug] });
                      setShowSectionForm(false);
                      setEditingSection(null);
                    }}
                  />
                )}
              </SheetContent>
            </Sheet>
            <Sheet open={!!openProjectFormSectionId || !!editingProject} onOpenChange={open => {
              if (!open) {
                setOpenProjectFormSectionId(null);
                setEditingProject(null);
                setEditingProjectSection(null);
              }
            }}>
              <SheetContent side="right" className="max-w-lg w-full">
                <SheetHeader>
                  <SheetTitle>
                    {editingProject ? "Edit Project" : "New Project"}
                  </SheetTitle>
                </SheetHeader>
                {openProjectFormSectionId ? (
                  <ProjectForm
                    section={
                      site.sections.find((s: any) => s.id === openProjectFormSectionId)
                    }
                    site={site}
                    allSections={site.sections}
                    onProjectCreated={() => {
                      queryClient.invalidateQueries({ queryKey: ["site", site.slug] });
                    }}
                    onClose={() => setOpenProjectFormSectionId(null)}
                    onDeleted={() => {
                      queryClient.invalidateQueries({ queryKey: ["site", site.slug] });
                      setOpenProjectFormSectionId(null);
                      setEditingProject(null);
                      setEditingProjectSection(null);
                    }}
                  />
                ) : editingProject ? (
                  <ProjectForm
                    section={editingProjectSection}
                    site={site}
                    allSections={site.sections}
                    project={editingProject}
                    onProjectCreated={() => {
                      queryClient.invalidateQueries({ queryKey: ["site", site.slug] });
                    }}
                    onClose={() => {
                      setEditingProject(null);
                      setEditingProjectSection(null);
                    }}
                    onDeleted={() => {
                      queryClient.invalidateQueries({ queryKey: ["site", site.slug] });
                      setOpenProjectFormSectionId(null);
                      setEditingProject(null);
                      setEditingProjectSection(null);
                    }}
                  />
                ) : null}
              </SheetContent>
            </Sheet>
          </>
        )}
      </div>
      
      {site && site.sections && (
        <div className="w-full">
          <Card className="p-4 bg-green-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold">Sections</h3>
            </div>
            <SectionsAccordion
              siteSlug={site.slug}
              sections={site.sections}
              onNewProject={sectionId => {
                setShowSectionForm(false);
                setEditingSection(null);
                setEditingProject(null);
                setEditingProjectSection(null);
                setOpenProjectFormSectionId(sectionId);
              }}
              openProjectFormSectionId={openProjectFormSectionId}
              onNewSection={() => {
                setShowSectionForm(true);
                setEditingSection(null);
                setEditingProject(null);
                setEditingProjectSection(null);
                setOpenProjectFormSectionId(null);
              }}
              onEditSection={section => {
                setEditingSection(section);
                setShowSectionForm(false);
                setEditingProject(null);
                setEditingProjectSection(null);
                setOpenProjectFormSectionId(null);
              }}
              onEditProject={(project, section) => {
                setEditingProject(project);
                setEditingProjectSection(section);
                setShowSectionForm(false);
                setEditingSection(null);
                setOpenProjectFormSectionId(null);
              }}
            />
          </Card>
        </div>
      )}
     
    </div>
  );
};


// Wrapper for SiteEditForm to provide form state and logic
const SiteEditFormWrapper: React.FC<{ site: any }> = ({ site }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [vercelModalOpen, setVercelModalOpen] = React.useState(false);

  // Fetch collaborators for deployer_user_id select
  const { data: collaborators = [], isLoading: loadingCollaborators } = useQuery({
    queryKey: ["site-collaborators", site?.id],
    queryFn: () => getSiteCollaborators(site.slug),
    enabled: !!site?.slug,
  });

  // Fetch Vercel projects for vercel_project_id select
  const { data: vercelProjects = [], isLoading: loadingVercelProjects } = useQuery({
    queryKey: ["site-vercel-projects", site?.id],
    queryFn: () => getSiteVercelProjects(site.slug),
    enabled: !!site?.slug,
  });

  // Form state
  const { register, handleSubmit, setValue, watch, formState, reset } = useForm({
    defaultValues: {
      name: "",
      subdomain: "",
      custom_domain: "",
      description: "",
      keywords: "",
      deployer_user_id: "",
      vercel_project_id: "",
      activated: false,
    },
  });

  React.useEffect(() => {
    if (site) {
      reset({
        name: site.name || "",
        subdomain: site.subdomain || "",
        custom_domain: site.custom_domain || "",
        description: site.description || "",
        keywords: site.keywords || "",
        deployer_user_id: site.deployer_user_id ? String(site.deployer_user_id) : "",
        vercel_project_id: site.vercel_project_id ? String(site.vercel_project_id) : "",
        activated: !!site.activated,
      });
    }
  }, [site, reset]);

  // Mutation for updating site
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const payload = {
        name: data.name,
        subdomain: data.subdomain,
        custom_domain: data.custom_domain,
        description: data.description,
        keywords: data.keywords,
        deployer_user_id: data.deployer_user_id,
        vercel_project_id: data.vercel_project_id,
        activated: data.activated,
      };
      const response = await put(`/sites/${site.slug}.json`, {
        body: JSON.stringify({ site: payload }),
        responseKind: "json",
      });
      if (!response.ok) {
        const error = await response.json;
        throw new Error(error.errors ? error.errors.join(", ") : "Failed to update site");
      }
      return response.json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site", site?.slug] });
      toast({
        title: "Site updated",
        description: "The site was updated successfully.",
        variant: "success",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error?.message || "Failed to update site.",
        variant: "destructive",
      });
    },
  });

  const values = watch();

  const handleFieldChange = (field: string, value: any) => {
    setValue(field as any, value, { shouldDirty: true });
  };

  const handleVercelProjectCreated = () => {
    if (site?.id) {
      queryClient.invalidateQueries({ queryKey: ["site-vercel-projects", site.id] });
    }
  };

  return (
    <SiteEditForm
      values={{
        name: values.name ?? "",
        subdomain: values.subdomain ?? "",
        custom_domain: values.custom_domain ?? "",
        description: values.description ?? "",
        keywords: values.keywords ?? "",
        deployer_user_id: values.deployer_user_id ? String(values.deployer_user_id) : "",
        vercel_project_id: values.vercel_project_id ? String(values.vercel_project_id) : "",
        activated: !!values.activated,
      }}
      onChange={handleFieldChange}
      onSubmit={handleSubmit((data) => mutation.mutate(data))}
      isPending={mutation.isPending}
      isError={mutation.isError}
      errorMessage={mutation.isError ? (mutation.error as Error)?.message : undefined}
      collaborators={(collaborators || []).map((user: any) => ({
        id: String(user.id),
        email: user.email,
        name: user.name,
      }))}
      loadingCollaborators={loadingCollaborators}
      vercelProjects={(vercelProjects || []).map((project: any) => ({
        id: String(project.id),
        name: project.name,
      }))}
      loadingVercelProjects={loadingVercelProjects}
      vercelModalOpen={vercelModalOpen}
      setVercelModalOpen={setVercelModalOpen}
      onVercelProjectCreated={handleVercelProjectCreated}
    />
  );
};

export default SiteShow;
